/**
 * The instruments one part is played on, and the id the file names each of them by.
 *
 * MusicXML says a doubling in two places at once: `<score-part>` lists every instrument the
 * player will pick up, and the bar where they pick one up points at it by id. So the roster
 * and the bars have to agree, and the agreement is worked out here once and read from both
 * sides rather than counted twice.
 *
 * Instruments are gathered rather than listed: a flute that takes the piccolo for sixteen
 * bars and then goes back to the flute is played on two instruments and not three, so the
 * bar that goes back points at the same id the part opened with. That is what every reader
 * expects, and it is what makes the file come back in as the two instruments it names.
 */

import type { InstrumentChange, Part, Score } from "../document/types";

/** One instrument a part is played on, as the file names it. */
export interface PlayedInstrument {
  /** The `<score-instrument>` id, e.g. `"P1-I2"`. */
  readonly id: string;
  readonly name: string;
  readonly sound: string | null;
  readonly midiProgram: number | null;
}

/** What a part's bars and its entry in the roster have to agree about. */
export interface InstrumentPlan {
  /** Every instrument the part is played on, the one it opens the piece on first. */
  readonly played: readonly PlayedInstrument[];
  /** The instrument taken up at a bar, by bar index, for the bars that take one up. */
  readonly changes: ReadonlyMap<number, PlayedInstrument>;
}

/** Two instruments are the same one when a player would call them that. */
function keyOf(instrument: InstrumentChange): string {
  const shift = instrument.transposition;
  const moved = `${shift.chromatic},${shift.diatonic},${shift.octaveChange}`;
  const sound = instrument.sound ?? "";
  const program = instrument.midiProgram ?? "";
  return [instrument.name, sound, program, moved].join("\u0000");
}

/** What a part is played on across the whole piece, from its roster entry to its bars. */
export function planInstruments(score: Score, part: Part, partId: string): InstrumentPlan {
  const played: PlayedInstrument[] = [];
  const known = new Map<string, PlayedInstrument>();
  const changes = new Map<number, PlayedInstrument>();

  const enrol = (instrument: InstrumentChange): PlayedInstrument => {
    const key = keyOf(instrument);
    const seen = known.get(key);
    if (seen !== undefined) return seen;
    const entry: PlayedInstrument = {
      id: `${partId}-I${played.length + 1}`,
      name: instrument.name,
      sound: instrument.sound,
      midiProgram: instrument.midiProgram,
    };
    known.set(key, entry);
    played.push(entry);
    return entry;
  };

  enrol(part.instrument);
  // A kit is a roster of its own -- one entry per drum, written by `export-drums.ts` -- and
  // a player cannot pick up another kit mid-piece the way they pick up a piccolo. So a drum
  // part is the one instrument it opened on, and a change written into one is not exported
  // rather than exported as an id nothing declares.
  if (part.instrument.percussion === null) {
    score.measures.forEach((measure, index) => {
      const change = part.measures[measure.id]?.instrument ?? null;
      if (change !== null) changes.set(index, enrol(change));
    });
  }

  return { played, changes };
}
